import { Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  VolumeButtons,
  VolumeButtonsCallback,
  VolumeButtonsOptions,
  VolumeButtonsResult,
} from '@capacitor-community/volume-buttons';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class VolumeButtonsService {
  private readonly volumeUpPressedSubject = new Subject<void>();
  public readonly volumeUpPressed$ = this.volumeUpPressedSubject.asObservable();
  private watchStarted = false;
  private watchRequested = false;
  private watchOperation: Promise<void> = Promise.resolve();
  private watchGeneration = 0;
  private activeWatchGeneration: number | null = null;

  constructor(private readonly zone: NgZone) {}

  async startWatching(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    this.watchRequested = true;
    await this.updateWatch();
  }

  async stopWatching(): Promise<void> {
    this.watchRequested = false;
    // Ignore callbacks immediately, even while native registration/cleanup is pending.
    this.activeWatchGeneration = null;
    await this.updateWatch();
  }

  private updateWatch(): Promise<void> {
    this.watchOperation = this.watchOperation.then(() => this.syncWatch());
    return this.watchOperation;
  }

  private async syncWatch(): Promise<void> {
    if (this.watchStarted && (!this.watchRequested || this.activeWatchGeneration === null)) {
      try {
        await VolumeButtons.clearWatch();
        this.watchStarted = false;
      } catch (error) {
        console.warn('[VolumeButtons] failed to clear volume watch', error);
        return;
      }
    }

    if (!this.watchRequested || this.watchStarted) {
      return;
    }

    const generation = ++this.watchGeneration;
    this.activeWatchGeneration = generation;
    let watchFailed = false;
    const options: VolumeButtonsOptions = {};
    const callback: VolumeButtonsCallback = (result: VolumeButtonsResult, err?: any) => {
      if (!this.watchRequested || this.activeWatchGeneration !== generation) {
        return;
      }

      if (err) {
        watchFailed = true;
        this.watchStarted = false;
        this.activeWatchGeneration = null;
        console.warn('[VolumeButtons] watchVolume error', err);
        return;
      }

      if (result?.direction !== 'up') {
        return;
      }

      this.zone.run(() => {
        this.volumeUpPressedSubject.next();
      });
    };

    try {
      await VolumeButtons.watchVolume(options, callback);
      // Callback-style native methods may report a registration error separately.
      this.watchStarted = !watchFailed;
    } catch (error) {
      this.activeWatchGeneration = null;
      console.warn('[VolumeButtons] failed to start volume watch', error);
    }
  }
}
